
import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper,
  SelectionProviderChildProps
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DownstreamOwnersFilteredByFieldQuery,
  DownstreamOwnersFilteredByFieldQueryVariables
} from '../../../gen-src/queries/DownstreamOwnersFilteredByFieldQuery';
import {
  VirtualConnectionTableDownstreamOwnersQuery,
  VirtualConnectionTableDownstreamOwnersQuery_virtualConnectionTables_downstreamOwnersConnection_nodes,
  VirtualConnectionTableDownstreamOwnersQueryVariables,
} from '../../../gen-src/queries/VirtualConnectionTableDownstreamOwnersQuery';
import { TableauUserSortOrder } from '../../../gen-src/queries/globalTypes';
import { DownstreamOwnersFilteredByFieldQueryGql } from '../../queries/DownstreamOwnersFilteredByFieldQueryGql';
import { VirtualConnectionTableDownstreamOwnersQueryGql } from '../../queries/VirtualConnectionTableDownstreamOwnersQueryGql';
import { OwnersDisplayGrid, OwnersDisplayGridRowData } from '../sharedDisplayGrid/OwnersDisplayGrid';
import { OwnersValidSortFields } from '../sorting/sortProviders/OwnersGridSortProvider';

type QueryVariables = VirtualConnectionTableDownstreamOwnersQueryVariables;
type QueryResults = VirtualConnectionTableDownstreamOwnersQuery;
type QueryResultListNode = VirtualConnectionTableDownstreamOwnersQuery_virtualConnectionTables_downstreamOwnersConnection_nodes;
type GridRowData = OwnersDisplayGridRowData;

type QueryVariablesWithFilter = DownstreamOwnersFilteredByFieldQueryVariables;
type QueryResultsWithFilter = DownstreamOwnersFilteredByFieldQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData?.virtualConnectionTables[0]?.downstreamOwnersConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter):
  ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.fieldsConnection && resultData.fieldsConnection.nodes &&
    resultData.fieldsConnection.nodes[0] && resultData.fieldsConnection.nodes[0].downstreamOwnersConnection;
}

function mapResultNode(node: QueryResultListNode): GridRowData {
  return {
    // NOTE: Our selection model uses the `id`, but the "send email" API needs the users' VizPortal IDs.
    // So this ID is actually the vizportal ID. TFS 891208 will allow us to use any node property as the selection ID.
    // Using the vizportalId in the interim should work fine, however, as long as we're not querying across sites.
    id: node.vizportalId,
    name: node.name,
    username: node.username,
    domain: node.domain,
    datasourceCount: node.ownedDatasourcesConnection ? node.ownedDatasourcesConnection.totalCount : null,
    workbookCount: node.ownedWorkbooksConnection ? node.ownedWorkbooksConnection.totalCount : null,
    metricsCount: node.ownedMetricsConnection ? node.ownedMetricsConnection.totalCount : null,
    virtualConnectionsCount: node.ownedVirtualConnectionsConnection ? node.ownedVirtualConnectionsConnection.totalCount : null
  };
}

export type VirtualConnectionTableDownstreamOwnersQueryGridProps =
  SelectionProviderChildProps &
  GridSortProps<OwnersValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly filteredColumnId: string | null; // Selected Field Key of the VirtualConnectionTable
    readonly rowCount: number | null;
  };

export function VirtualConnectionTableDownstreamOwnersQueryGrid(props: VirtualConnectionTableDownstreamOwnersQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamOwnersLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredOwnersLineageGridMessage();

  return props.filteredColumnId ? (
    // Query with selection
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={DownstreamOwnersFilteredByFieldQueryGql}
      partialQueryVariables={{ selectedFieldId: props.filteredColumnId, orderBy: props.orderBy as TableauUserSortOrder }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={mapResultNode}
    >
      {({ rowStore }) => (
        <OwnersDisplayGrid
          rowStore={rowStore}
          selectedKeys={props.selectedKeys}
          updateSelectionForKey={props.updateSelectionForKey}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      )}
    </PaginatedQueryWrapper>
  ) : (
      // Query without selection
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={VirtualConnectionTableDownstreamOwnersQueryGql}
        partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as TableauUserSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={mapResultNode}
      >
        {({ rowStore }) => (
          <OwnersDisplayGrid
            rowStore={rowStore}
            selectedKeys={props.selectedKeys}
            updateSelectionForKey={props.updateSelectionForKey}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    );
}
